import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App as AntApp } from "antd";
import { useLocation } from "react-router-dom";
import Cookies from "universal-cookie";
import { Button } from "../../design-system";
import { useAuth } from "../../context/AuthContext";
import {
    checkApiAvailability,
    getApiAvailabilityState,
    subscribeApiAvailability,
} from "../../utils/apiAvailability";
import { hasValidToken } from "../../utils/authSession";

const NOTIFICATION_KEY = "hrp-api-availability";
const POLL_INTERVAL_MS = 15000;
const VISIBILITY_RECHECK_MIN_INTERVAL_MS = 60000;
const HEALTH_CONFIRMATION_RETRY_MS = 3000;
const REQUIRED_HEALTH_CHECK_FAILURES = 2;
const PUBLIC_PATHS = new Set([
    "/login",
    "/dev-login",
    "/dev-quick-login",
    "/maintenance",
    "/client-section/login",
]);

export const isApiAvailabilityPublicPath = (pathname = "") => PUBLIC_PATHS.has(pathname);

const ApiAvailabilityWatcher = () => {
    const { notification } = AntApp.useApp();
    const { pathname } = useLocation();
    const authState = useAuth();
    const cookiesRef = useRef(new Cookies());
    const lastCheckStartedAtRef = useRef(0);
    const availabilityCheckInFlightRef = useRef(false);
    const [state, setState] = useState(getApiAvailabilityState);

    const shouldMonitor = useMemo(() => {
        if (isApiAvailabilityPublicPath(pathname)) return false;

        const cookies = cookiesRef.current;
        return (
            hasValidToken(authState?.jwtToken) ||
            hasValidToken(cookies.get("tokenR")) ||
            hasValidToken(cookies.get("tokenRC"))
        );
    }, [authState?.jwtToken, pathname]);

    const runAvailabilityCheck = useCallback(() => {
        if (availabilityCheckInFlightRef.current) {
            return;
        }

        availabilityCheckInFlightRef.current = true;
        lastCheckStartedAtRef.current = Date.now();
        void checkApiAvailability().finally(() => {
            availabilityCheckInFlightRef.current = false;
        });
    }, []);

    const retryNow = useCallback(() => {
        runAvailabilityCheck();
    }, [runAvailabilityCheck]);

    useEffect(() => subscribeApiAvailability(setState), []);

    useEffect(() => {
        if (!shouldMonitor) {
            notification.destroy(NOTIFICATION_KEY);
            return undefined;
        }

        runAvailabilityCheck();

        const interval = window.setInterval(() => {
            if (getApiAvailabilityState().status === "degraded") {
                runAvailabilityCheck();
            }
        }, POLL_INTERVAL_MS);

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                const currentState = getApiAvailabilityState();
                const lastCheckedAt = currentState.lastCheckedAt
                    ? Date.parse(currentState.lastCheckedAt)
                    : lastCheckStartedAtRef.current;
                const shouldRecheck = currentState.status === "degraded" ||
                    !lastCheckedAt ||
                    Date.now() - lastCheckedAt >= VISIBILITY_RECHECK_MIN_INTERVAL_MS;

                if (shouldRecheck) {
                    runAvailabilityCheck();
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [notification, runAvailabilityCheck, shouldMonitor]);

    useEffect(() => {
        if (!shouldMonitor || state.status !== "degraded" || state.detail?.source === "health_check") {
            return undefined;
        }

        runAvailabilityCheck();
        return undefined;
    }, [runAvailabilityCheck, shouldMonitor, state.detail?.source, state.status]);

    useEffect(() => {
        if (!shouldMonitor || state.status !== "degraded") {
            notification.destroy(NOTIFICATION_KEY);
            return undefined;
        }

        if (state.detail?.source !== "health_check") {
            return undefined;
        }

        if (state.consecutiveHealthCheckFailures < REQUIRED_HEALTH_CHECK_FAILURES) {
            const retryTimer = window.setTimeout(runAvailabilityCheck, HEALTH_CONFIRMATION_RETRY_MS);
            return () => window.clearTimeout(retryTimer);
        }

        notification.open({
            key: NOTIFICATION_KEY,
            message: "HRP is reconnecting",
            description:
                "Data may be temporarily unavailable while the service finishes an update. HRP is retrying automatically.",
            duration: 0,
            btn: (
                <Button type="primary" onClick={retryNow}>
                    Retry now
                </Button>
            ),
        });

        return undefined;
    }, [
        notification,
        retryNow,
        runAvailabilityCheck,
        shouldMonitor,
        state.consecutiveHealthCheckFailures,
        state.detail?.source,
        state.status,
    ]);

    return null;
};

export default ApiAvailabilityWatcher;
